import axios, { AxiosInstance } from 'axios';
import { useCallback } from 'react';
import { useAnimalHeaven } from '../context/animalHeaven.provider';
import { useNavigate } from 'react-router-dom';
import { notificationMessage } from './hooks';
import { Action } from './actions';
import { logout } from '../context/animalHeaven.actions';
import { useTranslation } from 'react-i18next';
import { getNamePathToPath } from '.';

const getOptions = (contentType: string, token: string, language: string) => ({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': contentType,
    'Access-Control-Allow-Origin': '*',
    'Accept-Language': language,
    ...(token ? { Authorization: token } : {}),
  },
});

const useApi = () => {
  const { i18n } = useTranslation();
  const { animalHeavenDispatch } = useAnimalHeaven();
  const navigate = useNavigate();
  const api = useCallback(
    (action: Action | FormData, { temporaryToken = '' } = {}) => {
      const token = localStorage.getItem('animal_heaven_token') ?? '';
      const api: AxiosInstance = axios.create(
        getOptions(
          action instanceof FormData ? 'multipart/form-data' : 'application/json',
          temporaryToken || token,
          i18n.language.toLocaleLowerCase()
        )
      );

      api.interceptors.request.use((config) => {
        return config;
      });

      api.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          if (error.response?.data?.message) {
            notificationMessage({
              message: error.response.data.message,
              type: 'error',
            });
          }

          // version problem, reload the app
          if (error?.response?.status === 403) {
            window.location.reload();
          }

          // expired token or token not valid
          if (error?.response?.status === 450 || error?.response?.status === 422) {
            animalHeavenDispatch(logout());
            navigate(getNamePathToPath('animals-in-heaven'));
          }

          return Promise.reject(error);
        }
      );

      if (action instanceof FormData) {
        action.append('appVersion', '0.0.0');
        return api.post('handleActionWithFiles', action);
      } else {
        return api.post('handleAction', {
          appVersion: '0.0.0',
          ...action,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { api };
};

export default useApi;
